// Types for the operate-playground REST API.
// Source of truth: docs/ui-plan.md §5.

export type PlaygroundStatus =
  | "provisioning"
  | "ready"
  | "destroying"
  | "failed"
  | "failed-interrupted";

// "upgrade" is "deploy ran in mode=upgrade" — appears on Playground.lastJob.kind
// so the UI can show "Upgrading X → Y" banner copy instead of "Provisioning…".
// Status flow is identical (provisioning → ready/failed).
export type JobKind = "deploy" | "upgrade" | "destroy";

export interface Me {
  upn: string;
  oid: string;
  roles: string[];
  /** Per ui-ux-design.md §implementation hand-off; lets the sticky job bar render w/o an extra fetch. */
  runningJobs: RunningJob[];
}

export interface RunningJob {
  customer: string;
  status: Extract<PlaygroundStatus, "provisioning" | "destroying">;
  step?: number;
  totalSteps?: number;
  jobId: string;
}

export interface Playground {
  customer: string;
  dnsZone: string;
  fqdn: string;
  status: PlaygroundStatus;
  createdBy: { oid: string; upn: string };
  createdAt: string;
  lastJob?: {
    id: string;
    kind: JobKind;
    startedAt: string;
    endedAt?: string;
  };
  sharedoVersion?: string;
  eeVersion?: string;
  ring?: string;
  /**
   * Admin username for a preconfigured environment (what the operator typed
   * into the wizard). Absent for seedAdminUser environments, where it's 'admin'.
   * Deliberately NOT the whole preconfigure payload - that holds the password in
   * plaintext and the server strips it.
   */
  adminUsername?: string;
  /** True when this environment was preconfigured via the StartupWizard. */
  preconfigured?: boolean;
  /** Convenience: which step the in-flight job is on. */
  step?: number;
  totalSteps?: number;
}

export interface PlaygroundStatusResponse {
  status: PlaygroundStatus;
  jobId?: string;
  step?: number;
  totalSteps?: number;
  /** Step ordinals the orchestrator has completed (1-based). */
  completedSteps?: number[];
  /** Last N stdout lines, newest last. */
  recentLog: string[];
  /** Optional: error summary when status === "failed". */
  failureSummary?: string;
  /** Server-side line cursor; pass back as ?since=<n>. */
  logCursor?: number;
  /**
   * True once the orchestrator has reported its first `[post]` marker —
   * the step counter doesn't advance past 11 during the multi-minute
   * post-deploy phase (migration, EE config import, doc repos, restart,
   * health probes). UI renders this as a synthetic "Post-deploy
   * configuration" row so users see active work is still happening
   * after step 11.
   */
  postPhase?: boolean;
}

export interface CreatePlaygroundRequest {
  customer: string;
  dnsZone: string;
  sharedoVersion?: string;
  eeVersion?: string;
  ring?: string;
  /**
   * Opt-in admin-user seeding (task AL). When true, the orchestrator's
   * final step provisions a single 'admin' user (KV-backed password) and
   * removes the playground build's seeded demo users. Default false.
   * Newer playground builds derived from blank-sharedo ship their own
   * StartupWizard for first-admin bootstrap; leave this unchecked for
   * those. Older builds need it ticked.
   */
  seedAdminUser?: boolean;
  /**
   * Sidecar backend for ES + EventStore (admin-only opt-in). "aci" (default) runs the per-customer
   * ACI ContainerGroups; "aks" places them on the shared AKS cluster (durable PVCs + pinned
   * internal-LB IPs). Omit/"aci" for normal deploys.
   */
  sidecarBackend?: "aci" | "aks";
  /**
   * .NET 10 search/indexer tier (searchV2 — admin-only opt-in). When true the customer deploys
   * the clean-swap v2 tier: secured Elasticsearch 9 + the net10 indexer-v2/search-v2 container
   * apps, with ES 6 + the 4.8 indexer dropped. Requires the selected release to carry the net10
   * package (net10-components.json). Omit/false for normal legacy ES 6 deploys.
   *
   * NOTE: platformUpgrade implies this server-side (the umbrella flag forces searchV2 on).
   */
  searchV2?: boolean;
  /**
   * Umbrella platform-upgrade opt-in (`sr-operate-platform-upgrade` — admin-only). When true the
   * customer deploys the fully-upgraded platform: KurrentDB 26.0 (gRPC/TLS) instead of EventStore
   * 4.1.2, OpenIddict identity (in the build), and the ES 9 / net10 indexer-v2 + search-v2 tier
   * (implies searchV2). Requires the selected release to be a platform-upgrade build. Omit/false
   * for normal legacy deploys.
   */
  platformUpgrade?: boolean;
  /**
   * "Preconfigure the environment" (task BP). The StartupWizard answers the
   * operator filled in on the create form; the orchestrator replays them
   * against the trial once it's healthy, so nobody has to click through the
   * wizard by hand.
   *
   * Setting this implies `seedAdminUser: false` server-side — the wizard's own
   * admin-user section creates the administrator.
   *
   * Mirrors `PreconfigureConfig` in src/lib/preconfigure.ts (the web app builds
   * separately, so the shape is duplicated rather than imported). Keep in sync.
   */
  preconfigure?: PreconfigureConfig;
}

/** Mirrors `PreconfigureConfig` in src/lib/preconfigure.ts — keep in sync. */
export interface PreconfigureConfig {
  organisation: {
    name: string;
    /** Letters and numbers only — the wizard rejects anything else. */
    shortName: string;
  };
  localisation: {
    /** `config.countries.systemName`, e.g. "gbr". */
    countrySystemName: string;
    /** `config.currencies.isoCode`, e.g. "GBP". */
    currencyIsoCode: string;
    /** `config.languages.isoCode` (ISO 639-2), e.g. "eng". */
    languageIsoCode: string;
  };
  adminUser: {
    firstname: string;
    surname: string;
    username: string;
    password: string;
  };
  /** Config-pack systemNames to import, e.g. ["core-config-pack"]. */
  packages: string[];
}

/**
 * Body shape for POST /api/playgrounds/:customer/upgrade.
 *
 * - { refresh: true } — re-apply current ring/version
 * - { version: "x.y.z", eeVersion?: "…" } — bump to a new release
 * - `seedAdminUser` opts into the orchestrator's admin-user seed step on
 *   the upgrade pass (skips the demo-user wipe — only refreshes the
 *   admin password). Mirrors CreatePlaygroundRequest.seedAdminUser.
 */
export interface UpgradePlaygroundRequest {
  version?: string;
  eeVersion?: string;
  refresh?: boolean;
  seedAdminUser?: boolean;
}

export interface CreatePlaygroundResponse {
  jobId: string;
  customer: string;
}

export interface DnsZone {
  zone: string;
  /** Default selection. */
  default?: boolean;
}

/** Operate version available for deploy. Maps to ring=staging or ring=playground. */
export interface SharedoVersion {
  version: string;
  publishedAt: string;
}

export interface SharedoVersionsResponse {
  versions: SharedoVersion[];
  /** Set when the container couldn't be listed (e.g., empty ring, perms). UI surfaces this. */
  warning?: string;
}

/**
 * Response from `GET /api/playgrounds/:customer/seq-credentials`.
 *
 * Fetched lazily on first "Reveal" click — NOT included in the regular
 * status poll so the plain-text password doesn't ride along with every
 * 5-second tick. Returns 404 with `error: "seq-credentials-not-provisioned"`
 * for customers deployed before the per-customer-Seq feature landed; the
 * UI renders an empty state instructing a Retry/Upgrade.
 */
export interface SeqCredentialsResponse {
  /** `https://{customer}-seq.{dnsZone}` — link target. */
  url: string;
  /** Always the literal string `"admin"`. */
  username: string;
  /** Plain text Seq admin password from Key Vault. */
  password: string;
}

/**
 * Response from `GET /api/playgrounds/:customer/admin-credentials`.
 *
 * Symmetric with SeqCredentialsResponse — same lazy-on-reveal pattern, same
 * 404 body shape (`error: "admin-credentials-not-provisioned"`) when the
 * customer was deployed without `seedAdminUser: true` (the default) or
 * before the feature landed.
 */
export interface AdminCredentialsResponse {
  /** `https://{customer}.{dnsZone}/` — link target (the trial site itself). */
  url: string;
  /**
   * Usually the literal `"admin"` (seedAdminUser's identityClaim). For a
   * preconfigured environment (task BP) it's the username the operator entered
   * into the Startup Wizard form, read back from Key Vault.
   */
  username: string;
  /** Plain text admin password from Key Vault. */
  password: string;
}

// Typed errors -------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = "Unauthorized", body?: unknown) {
    super(401, message, body);
    this.name = "UnauthorizedError";
  }
}

export class ForbiddenError extends ApiError {
  constructor(message = "Forbidden", body?: unknown) {
    super(403, message, body);
    this.name = "ForbiddenError";
  }
}

export class ConflictError extends ApiError {
  constructor(message = "Conflict", body?: unknown) {
    super(409, message, body);
    this.name = "ConflictError";
  }
}
